import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from './AnimatePresenceWrapper';
import { useCart } from '../../shared/context/CartContext';
import api from '../../shared/services/api';
import { Product } from '../../shared/types';
import ProductCard from './ProductCard';

interface PopupMinimumRequirement {
  type: string;
  value: number;
  formattedText: string;
}

interface ActivePopupResponse {
  discountId: string;
  name: string;
  description: string;
  value: number;
  type: string;
  triggerType: string;
  delaySeconds: number;
  maxShows?: number | null;
  appliesTo?: string;
  minimumRequirement?: PopupMinimumRequirement | null;
  collectionId?: string | null;
  collectionName?: string | null;
  collectionImageUrl?: string | null;
  products: (Product & { id: string })[];
}

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.lg};
  box-sizing: border-box;
`;

const Modal = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  border-radius: 16px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.06);
`;

const Header = styled.div`
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl} ${props => props.theme.spacing.md};
  text-align: center;
  border-bottom: 1px solid ${props => props.theme.colors.border?.light || '#eee'};
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.black};
  color: ${props => props.theme.colors.white};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 4px;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text?.primary || '#111'};
  line-height: 1.3;
`;

const Description = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.text?.secondary || '#666'};
  margin: 0;
`;

const MinimumRequirementText = styled.p`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text?.primary || '#111'};
  margin: ${props => props.theme.spacing.sm} 0 0;
`;

const CollectionLine = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text?.secondary || '#666'};
  margin: ${props => props.theme.spacing.xs} 0 0;
`;

const WhatToDo = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text?.secondary || '#666'};
  margin: ${props => props.theme.spacing.sm} 0 0;
  line-height: 1.45;
`;

const QuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  justify-content: center;
  padding: 0 ${props => props.theme.spacing.lg} ${props => props.theme.spacing.lg};
`;

const QuickActionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.black};
  background: ${props => props.theme.colors.white};
  border: 2px solid ${props => props.theme.colors.black};
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.15s, color 0.15s, transform 0.1s;

  &:hover {
    background: ${props => props.theme.colors.black};
    color: ${props => props.theme.colors.white};
    transform: translateY(-1px);
  }
`;

const HeroImage = styled(motion.div)`
  width: 100%;
  aspect-ratio: 21 / 9;
  min-height: 120px;
  overflow: hidden;
  background: ${props => props.theme.colors.background?.secondary || '#f5f5f5'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeroLink = styled(Link)`
  display: block;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: inherit;
`;

const ProductsSectionTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  margin: 0 0 ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text?.primary || '#111'};
  padding: 0 ${props => props.theme.spacing.lg};
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.lg};
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  & > * {
    min-width: 0;
  }

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border?.light || '#eee'};
  background: ${props => props.theme.colors.background?.secondary || '#fafafa'};
`;

const Button = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 16px 24px;
  font-size: 0.9375rem;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease;
  border: none;
  letter-spacing: 0.02em;

  ${props => props.$primary
    ? `
      background: ${props.theme.colors.black};
      color: ${props.theme.colors.white};
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    `
    : `
      background: ${props.theme.colors.white};
      color: ${props.theme.colors.text?.primary || '#111'};
      border: 2px solid ${props.theme.colors.border?.light || '#ddd'};
    `}

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
    ${props => props.$primary ? 'box-shadow: 0 4px 12px rgba(0,0,0,0.2);' : ''}
  }
  &:active {
    transform: translateY(0);
    opacity: 1;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  inset-inline-end: ${props => props.theme.spacing.md};
  width: 44px;
  height: 44px;
  border: none;
  background: rgba(0,0,0,0.06);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  line-height: 1;
  color: ${props => props.theme.colors.text?.secondary || '#666'};
  z-index: 2;
  transition: background 0.2s, color 0.2s, transform 0.15s;

  &:hover {
    background: rgba(0,0,0,0.1);
    color: ${props => props.theme.colors.text?.primary || '#111'};
    transform: scale(1.05);
  }
  &:active {
    transform: scale(0.98);
  }
`;

const ModalWrapper = styled.div`
  position: relative;
`;

const ProductLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
`;

export default function DiscountPopup() {
  const { t } = useTranslation();
  const { cart } = useCart();
  const [popup, setPopup] = useState<ActivePopupResponse | null>(null);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const triggerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Prevents re-triggering OnPageLoad / TimeDelay on subsequent cart-change re-fetches
  const hasShownRef = useRef(false);
  const hasShownForAddToCartRef = useRef(false);
  const prevCartCountRef = useRef(0);
  const pendingAddToCartRef = useRef(false);

  const fetchActive = useCallback(async () => {
    try {
      const params: { cartTotal?: number; productIds?: string } = {};
      if (cart && cart.items.length > 0) {
        params.cartTotal = cart.subtotal;
        params.productIds = cart.items.map(i => i.productId).join(',');
      }
      const { data } = await api.get<ActivePopupResponse | null>('/popup/active', { params });
      return data;
    } catch (e) {
      console.error('Failed to fetch popup', e);
      return null;
    }
  }, [cart?.subtotal, cart?.items?.length]);

  const trackShown = useCallback(async (discountId: string) => {
    try {
      await api.post('/popup/shown', { discountId });
    } catch (e) {
      console.error('Failed to track popup shown', e);
    }
  }, []);

  const dismiss = useCallback(async (discountId: string) => {
    try {
      await api.post('/popup/dismiss', { discountId });
    } catch (e) {
      console.error('Failed to dismiss popup', e);
    }
    setVisible(false);
    setPopup(null);
  }, []);

  const cartCount = cart?.items?.length ?? 0;

  // Track cart count changes for AddToCart trigger
  useEffect(() => {
    if (cartCount > prevCartCountRef.current) {
      pendingAddToCartRef.current = true;
    }
    prevCartCountRef.current = cartCount;
  }, [cartCount]);

  // Fetch popup on mount and when cart changes (for AddToCart trigger)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchActive().then((data) => {
      if (cancelled) return;
      setLoading(false);
      if (!data || !data.name) {
        setPopup(null);
        return;
      }
      setPopup(data);
    });
    return () => { cancelled = true; };
  }, [fetchActive]);

  // Trigger display based on triggerType and delay
  useEffect(() => {
    if (!popup || loading) return;

    const triggerType = (popup.triggerType || 'TimeDelay').toLowerCase();
    const delaySeconds = Math.max(0, popup.delaySeconds ?? 0);

    if (triggerType === 'onpageload') {
      // Only trigger once per component lifetime (don't re-show on cart changes)
      if (hasShownRef.current) return;
      hasShownRef.current = true;
      triggerTimeoutRef.current = setTimeout(() => {
        setVisible(true);
        trackShown(popup.discountId);
      }, 600);
      return () => {
        if (triggerTimeoutRef.current) clearTimeout(triggerTimeoutRef.current);
      };
    }

    if (triggerType === 'timedelay') {
      // Only trigger once per component lifetime (don't restart timer on cart changes)
      if (hasShownRef.current) return;
      hasShownRef.current = true;
      const delayMs = (delaySeconds || 20) * 1000;
      triggerTimeoutRef.current = setTimeout(() => {
        setVisible(true);
        trackShown(popup.discountId);
      }, delayMs);
      return () => {
        if (triggerTimeoutRef.current) clearTimeout(triggerTimeoutRef.current);
      };
    }

    if (triggerType === 'addtocart' && cart && cart.items.length > 0 && pendingAddToCartRef.current) {
      if (!hasShownForAddToCartRef.current) {
        hasShownForAddToCartRef.current = true;
        pendingAddToCartRef.current = false;
        setVisible(true);
        trackShown(popup.discountId);
      }
    }
  }, [popup, loading, cart?.items?.length, trackShown]);

  if (!popup) return null;

  const products: Product[] = (popup.products || []).map((p: any) => ({
    ...p,
    id: typeof p.id === 'string' ? p.id : (p.id ?? '').toString(),
    imageUrls: Array.isArray(p.imageUrls) ? p.imageUrls : (p.ImageUrls ?? []),
    createdAt: p.createdAt ?? new Date().toISOString(),
  }));

  const minReq = popup.minimumRequirement;
  const requirementLine = minReq?.formattedText
    ? (popup.collectionName && minReq.type === 'quantity'
      ? t('discountPopup.fromCollection', { text: minReq.formattedText, name: popup.collectionName })
      : minReq.formattedText)
    : null;

  const collectionId = popup.collectionId != null ? String(popup.collectionId) : null;
  const collectionUrl = collectionId ? `/collections/${collectionId}` : null;
  const primaryUrl = collectionUrl ?? '/products';
  const primaryLabel = popup.collectionName
    ? t('discountPopup.shopNamed', { name: popup.collectionName })
    : t('discountPopup.shopOffer');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 },
    },
    exit: { opacity: 0 },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <AnimatePresence>
      {visible && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={() => dismiss(popup.discountId)}
        >
          <ModalWrapper onClick={(e) => e.stopPropagation()}>
            <Modal
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <CloseButton type="button" onClick={() => dismiss(popup.discountId)} aria-label={t('discountPopup.close')}>
                ×
              </CloseButton>
              <Header>
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Badge>{t('discountPopup.limitedOffer')}</Badge>
                  <Title>{popup.name}</Title>
                  <Description>{popup.description}</Description>
                  {requirementLine && (
                    <MinimumRequirementText>{requirementLine}</MinimumRequirementText>
                  )}
                  {popup.collectionName && !requirementLine && (
                    <CollectionLine>{t('discountPopup.appliesTo', { name: popup.collectionName })}</CollectionLine>
                  )}
                  <WhatToDo>
                    {collectionUrl
                      ? t('discountPopup.whatToDoCollection')
                      : t('discountPopup.whatToDoGeneric')}
                  </WhatToDo>
                </motion.div>
              </Header>
              {(collectionUrl || products.length > 0) && (
                <QuickActions>
                  {collectionUrl && (
                    <QuickActionLink to={collectionUrl} onClick={() => dismiss(popup.discountId)}>
                      {t('discountPopup.viewCollection', { name: popup.collectionName || t('discountPopup.collectionAlt') })}
                    </QuickActionLink>
                  )}
                  {products.length > 0 && (
                    <QuickActionLink to={collectionUrl || '/products'} onClick={() => dismiss(popup.discountId)}>
                      {t('discountPopup.seeProducts', { count: products.length })}
                    </QuickActionLink>
                  )}
                </QuickActions>
              )}
              {popup.collectionImageUrl && (
                <HeroImage
                  as={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                >
                  {collectionUrl ? (
                    <HeroLink to={collectionUrl} onClick={() => dismiss(popup.discountId)}>
                      <img src={popup.collectionImageUrl} alt={popup.collectionName ?? t('discountPopup.collectionAlt')} />
                    </HeroLink>
                  ) : (
                    <img src={popup.collectionImageUrl} alt={popup.collectionName ?? t('discountPopup.collectionAlt')} />
                  )}
                </HeroImage>
              )}
              {products.length > 0 && (
                <>
                  <ProductsSectionTitle as={motion.h3} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                    {popup.collectionName ? t('discountPopup.productsFrom', { name: popup.collectionName }) : t('discountPopup.productsInOffer')}
                  </ProductsSectionTitle>
                  <ProductsGrid>
                    <motion.div
                      style={{ display: 'contents' }}
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      {products.slice(0, 4).map((product) => (
                        <motion.div key={product.id} variants={itemVariants} style={{ minWidth: 0 }}>
                          <ProductLink
                            to={`/products/${product.handle || product.id}`}
                            onClick={() => dismiss(popup.discountId)}
                          >
                            <ProductCard product={product} isLarge={false} isNew={false} />
                          </ProductLink>
                        </motion.div>
                      ))}
                    </motion.div>
                  </ProductsGrid>
                </>
              )}
              <Actions as={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>
                <Button $primary as={Link} to={primaryUrl} onClick={() => dismiss(popup.discountId)}>
                  {primaryLabel}
                </Button>
                <Button as={Link} to="/products" onClick={() => dismiss(popup.discountId)}>
                  {collectionUrl ? t('discountPopup.browseAll') : t('discountPopup.browseProducts')}
                </Button>
                <Button type="button" onClick={() => dismiss(popup.discountId)}>
                  {t('discountPopup.noThanks')}
                </Button>
              </Actions>
            </Modal>
          </ModalWrapper>
        </Overlay>
      )}
    </AnimatePresence>
  );
}
